/**
 * Presentation rules shared by every screen — the one place a score becomes a
 * label and a colour, a date becomes "3 days ago", cents become "US$19".
 * Ported from the approved prototype's dashboard logic so the thresholds and
 * copy are identical everywhere (listing, detail, tracking, previews).
 */
import type { HealthFix, HealthResult, HealthRow, HealthRowKey, HealthSuggestions, JobMatch, MatchConfidence, MatchEligibility, TailorLevel, TailorLevelPlan, TemplateId, RankedFix } from '../api/types';

// ── match tiers: 76 / 61 / 36, derived from the score only (never sent by the server) ──
// The server's own bands, `settings.MATCH_TIERS` (re-baselined from 80/65/40 by
// resume-builder-tailor ticket 07; this copy was left behind until ux-bug-pass
// ticket 28's follow-up, so a server-Strong 76–79 read "Good Match" here). The
// server decides Recommended, the recommended tailor level and the ineligible
// cap (Strong − 1) from them, so a label here must mean the same number.
// Pinned by `backend/tests/contract/test_match_tiers_drift.py`.
export interface MatchTier {
  min: number;
  label: string;
  short: string;
  color: string;
  ink: string;
  bg: string;
  line: string;
  score: number;
  scoreLabel: string;
}
const MATCH_TIERS = [
  { min: 76, label: 'Strong Match', short: 'Strong', color: '#16A34A', ink: '#15803D' },
  { min: 61, label: 'Good Match', short: 'Good', color: '#2563EB', ink: '#1D4ED8' },
  { min: 36, label: 'Partial Match', short: 'Partial', color: '#D97706', ink: '#B45309' },
  { min: 0, label: 'Low Match', short: 'Low', color: '#DC2626', ink: '#B91C1C' },
];
export function matchTier(v: number | null | undefined): MatchTier {
  const n = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
  const t = MATCH_TIERS.find((x) => n >= x.min) || MATCH_TIERS[3];
  return { ...t, score: n, bg: 'color-mix(in srgb, ' + t.color + ' 12%, #fff)', line: 'color-mix(in srgb, ' + t.color + ' 32%, #fff)', scoreLabel: t.label + ' · ' + n + '/100' };
}
export const rangeColor = (v: number) => matchTier(v).color;
export function toneVarsForScore(v: number) {
  const t = matchTier(v);
  // The tier's ink, 8% darker: Partial (#B45309) on its tint was 4.42:1 and Strong 4.39:1,
  // under AA; this is about 5.0:1 on all four (career-compass-launch ticket 21). The tier
  // inks themselves stay for the many other chips that use `matchTier`.
  return { bg: t.bg, ink: 'color-mix(in srgb, ' + t.ink + ' 92%, #000)', bar: t.color };
}

/**
 * The Job search score's own bands (ux-bug-pass-6 ticket 07, D11). Same edges as
 * the job-match tiers, other words, no red. Pinned to the backend's
 * `settings.INSIGHTS_BANDS`; job cards keep `matchTier`. Colours are the app's
 * existing tokens: muted on the sunken canvas, watch, accent, good.
 */
const SCORE_BANDS = [
  { min: 76, label: 'Strong', bg: 'var(--good-soft)', ink: 'var(--good-ink)', bar: 'var(--good)' },
  { min: 61, label: 'On track', bg: 'var(--accent-soft)', ink: 'var(--accent-ink)', bar: 'var(--accent)' },
  { min: 36, label: 'Building', bg: 'var(--watch-soft)', ink: 'var(--watch-ink)', bar: 'var(--watch)' },
  { min: 0, label: 'Just starting', bg: 'var(--canvas)', ink: 'var(--ink-muted)', bar: 'var(--ink-muted)' },
];
export function scoreBand(v: number) {
  const n = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
  return SCORE_BANDS.find((b) => n >= b.min) || SCORE_BANDS[3];
}

// ── eligibility, confidence, withheld credits: what the score knows and the
//    band alone cannot say (Builder/Tailor ticket 27) ──
/**
 * The badge for a job the account could not take as things stand.
 *
 * `null` for `eligible` — and for an absent reading, which is what a job with
 * no match block at all gives (`m?.eligibility`, not an older backend:
 * API_SPEC sends the field for every reading). **Never a neutral chip**: the
 * badge exists because an
 * ineligible job is capped below Strong (`MATCH_INELIGIBLE_SCORE_CAP`) and so
 * drops quietly out of Recommended, and a chip that also appears when
 * everything is fine says nothing.
 *
 * **Two colours, one per reading** (ux-bug-pass-2 ticket 14, reversing
 * resume-builder-tailor ticket 27's single amber): `relocation_required` is
 * `--risk`, the Severity colors High pair, because needing to move country is
 * the biggest practical obstacle a job carries and amber read as a minor
 * caution; it is red in every state, tailored or not. `remote_restricted` stays
 * `--watch`: the job is constrained, not closed. Colour **and** text, always.
 *
 * **A third, neutral reading** (skill-bank-tailor ticket 08, D14):
 * `location_unclear` when the job's country, its remote scope or the resume's
 * country is unknown. Grey, not amber: nothing is wrong, we just do not know,
 * and the server never caps it. It always carries its reason line.
 *
 * Any other value, including one a newer server adds, draws nothing rather
 * than a wrong badge.
 */
export function eligibilityBadge(reading: MatchEligibility | string | undefined): { label: string; note: string; bg: string; ink: string; line: string } | null {
  if (reading === 'relocation_required') {
    const risk = { bg: 'var(--risk-soft)', ink: 'var(--risk-ink)', line: 'color-mix(in srgb, var(--risk) 35%, #fff)' };
    return { ...risk, label: "You'd need to move country", note: 'This role is on-site in a country other than the one on your resume.' };
  }
  if (reading === 'remote_restricted') {
    const watch = { bg: 'var(--watch-soft)', ink: 'var(--watch-ink)', line: 'color-mix(in srgb, var(--watch) 35%, #fff)' };
    return { ...watch, label: 'Remote, but not from your country', note: 'The posting limits remote work to countries that do not include yours.' };
  }
  if (reading === 'location_unclear') {
    return { bg: 'var(--canvas)', ink: 'var(--ink-secondary)', line: 'var(--line-strong)', label: 'Location unclear', note: 'We could not tell where this job is based.' };
  }
  return null;
}
/**
 * The thin-posting line: the score is not wrong, it is just standing on less.
 * Deliberately a quiet note and not a warning — `confidence` says what the
 * *posting* carried, never what the candidate did.
 */
export function confidenceNote(confidence: MatchConfidence | undefined): string | null {
  return confidence === 'low' ? 'This posting lists no skills, so the score stands on its title alone.' : null;
}
/**
 * The `withheldMatches` line: skills the score counted that a Match-level
 * tailored resume may not carry.
 *
 * ⚠️ **It hedges on purpose.** The flag is a scorer's-eye view and errs toward
 * warning: it sees only the chips `extract_features` cached, where the
 * tailor's `allowed_skills` scans the whole document — so a skill written into
 * a bullet but never listed is flagged here and would in fact survive a Match
 * tailor. "may not carry" is therefore the strongest true claim; "will be
 * dropped" would be false for exactly that case.
 *
 * It is phrased over *matched skills*, not over the chips on screen: the list
 * beside it is truncated at six and the count is over all of them.
 */
export function withheldNote(count: number | undefined): string | null {
  if (!count || count < 1) return null;
  const subject = count === 1 ? 'One of your matched skills is not' : count + ' of your matched skills are not';
  const object = count === 1 ? 'carry it' : 'carry them';
  return subject + " in your resume's own skills list, so a Match-level tailor may not " + object + '. Max Match reaches further.';
}

// ── the experience statement (Builder/Tailor ticket 26) ──
export interface ExperienceStatement {
  /** The Meets / Below / Not stated verdict someone acts on. */
  verdict: string;
  /** The same verdict for a cramped breakdown cell. */
  short: string;
  detail: string;
  icon: string;
  ink: string;
}
/**
 * One statement about experience: the verdict, and a line that is true of
 * whatever `exp.years` turned out to be.
 *
 * Since Builder/Tailor ticket 07 `exp.years` is the figure the requirement was
 * *judged against*, which is years inside the job's own title family wherever
 * the two differ — and the wire does not say which reading it is. It falls
 * back to the whole career at face value when the posting's family cannot be
 * read, when the cached features predate `FEATURES_VERSION` 4, or when no role
 * carries a parsed date. So the neutral line says what the number *is* ("years
 * counted for this role") rather than claiming a scope that is wrong half the
 * time.
 *
 * ⚠️ **The out-of-field case is derived, not sent.** A career changer gets the
 * whole career back as `years` *and* `ok: false` however long it is, because
 * `OUT_OF_FIELD_YEARS_CREDIT` caps the component rather than discounting the
 * years. That is the one case where `ok` is false with `years >= req`, and
 * without naming it the panel reads "12 years · 5+ years required · Below
 * requirement", which looks like we cannot count. The inference holds because
 * that credit is the only thing in `_score_experience` that can hold the
 * component under 1.0 once the ratio is met; a second discount there would
 * make this line wrong, and the honest fix then is a scope on the wire.
 */
export function experienceStatement(exp: NonNullable<JobMatch['exp']> | null | undefined): ExperienceStatement {
  const muted = { ink: 'var(--ink-muted)', icon: '' };
  const met = { ink: '#15803D', icon: '✓' };
  const short = { ink: '#B45309', icon: '↓' };
  // ⚠️ **No match block and a block with no requirement are different
  // sentences**, and the decision panel is not gated on `hasMatch` — only the
  // skills section below it is. Collapsed into one arm, an account with no
  // master résumé read "This posting states no experience requirement" on
  // every posting, which is a false claim about the *job*.
  if (!exp) {
    return { ...muted, verdict: 'Not scored yet', short: 'Not scored', detail: 'We have not measured your experience against this role yet.' };
  }
  // A posting that states no minimum sends `exp` with a null `req` inside it,
  // never a null `exp`, so this branches on the requirement. Branching on the
  // block is what printed "null+ years required".
  if (exp.req == null || exp.req <= 0) {
    return { ...muted, verdict: 'Not stated', short: 'Not stated', detail: 'This posting states no experience requirement.' };
  }
  const req = exp.req + '+ ' + (exp.req === 1 ? 'year' : 'years') + ' required';
  // Unreachable from `rules.ExperienceMatch`, whose `years` is a plain float;
  // reachable from a NULL `job_matches.exp_years` the wire types for. It
  // prints the requirement and **states no cause** — the wire says only that
  // the figure is absent, never why.
  if (exp.years == null) {
    return { ...muted, verdict: 'Not stated', short: 'Not stated', detail: req };
  }
  const years = pluralize(exp.years, 'year');
  const ok = exp.ok === true;
  const outOfField = !ok && exp.years >= exp.req;
  return {
    ...(ok ? met : short),
    verdict: ok ? 'Meets experience requirement' : 'Below requirement',
    short: ok ? 'Meets' : 'Below',
    detail: (outOfField ? years + ' of experience, none of it in this field' : years + ' counted for this role') + ' · ' + req,
  };
}

// ── status tones (good / watch / attention) ──
/** The three-way reading of a 0–100 score (`CONTEXT.md`, Band). */
export type Band = 'Good' | 'Watch' | 'Needs attention';
/**
 * A score's Band. Good begins at the benchmark, Watch at 50.
 *
 * Pass the wire `benchmark` wherever the payload carries one, so the Band and
 * any "our bar" copy beside it read one threshold. The default is the pinned
 * copy for screens whose payload has none; `test_health_benchmark_drift.py`
 * holds it to `settings.HEALTH_BENCHMARK`.
 */
export const PINNED_BENCHMARK = 80;
export function statusOf(pct: number, benchmark = PINNED_BENCHMARK): { tone: 'good' | 'watch' | 'attention'; label: Band } {
  return pct >= benchmark ? { tone: 'good', label: 'Good' } : pct >= 50 ? { tone: 'watch', label: 'Watch' } : { tone: 'attention', label: 'Needs attention' };
}
type BandTone = ReturnType<typeof statusOf>['tone'];
export function toneVars(tone: BandTone) {
  return tone === 'good'
    ? { bg: 'var(--good-soft)', ink: 'var(--good-ink)', bar: 'var(--good)' }
    : tone === 'watch'
      ? { bg: 'var(--watch-soft)', ink: 'var(--watch-ink)', bar: 'var(--watch)' }
      : { bg: 'var(--risk-soft)', ink: 'var(--risk-ink)', bar: 'var(--risk)' };
}
/**
 * What a surface wears where there is no Band to wear, in `toneVars`' shape: a
 * Dimension's "Not measured" chip and the flags card (`dimensions.ts`), a
 * report with no overall (resume-360 ticket 20), a score that is not there.
 * The ink is the shell's `--ink-secondary` (the same #4a4c60), with the value
 * as its fallback because the funnel root defines only the severity tokens.
 * The fill has no token anywhere: it is the prototype's neutral chip fill, and
 * the shell's nearest (`--surface-sunken`) is a different grey, so no screen
 * builds its own neutral on it.
 */
export const NEUTRAL = { bg: '#f3f4f7', ink: 'var(--ink-secondary, #4a4c60)', bar: 'var(--line-strong)' } as const;

// ── a report's overall, read against the bar once (resume-360 tickets 08, 20) ──
/**
 * A report's standing: its Band's tone, or `not_scored` when its overall was
 * not measured, which is the absence of a Band, never a fourth one. Analytics
 * sends it as `overall_band`, spec §9's wire name.
 */
export type ReportStanding = BandTone | 'not_scored';
/** What every surface with no overall to show says, in one set of words (resume-360 ticket 20). */
export const NOT_SCORED_LINE = "There isn't enough in your resume to score yet.";
/**
 * The detector id of the fix a document with no experience bullets gets
 * (`analysis.rules`): the wire's own "this has no experience bullets". It ranks
 * first whenever it fires, so it is always among `fixes`, which every report of
 * every age carries (`backend/tests/contract/test_no_bullets_cap_drift.py`).
 */
export const NO_EXPERIENCE_BULLETS_FIX = 'no_experience_bullets';
/**
 * What a surface says, where its verdict or its gap would go, of a report the
 * no-bullets cap holds at Watch: its number is at or above the bar, and it is
 * not "above our bar", nor any number of points from it.
 */
export const NO_BULLETS_CAP_LINE = "Your resume can't clear our bar until it has experience bullets.";
/**
 * The overall, its gap, its standing and its Band, or none of them. A union
 * rather than nullables, so no branch can hold a gap or a Band without the
 * score it was read from.
 *  - `score: null`: not measured, since the measured Dimensions carry under
 *    half the weight (ticket 20, `rules.MEASURED_WEIGHT_FLOOR`).
 *  - `capped`: at or above the bar with no experience bullets (ticket 20's
 *    review, decided by the user 2026-09-30). Still scored and its number
 *    shown, but Watch: no Good Band, no "above our bar", and no gap, since
 *    `benchmark − score` would be a negative distance to a bar it cannot
 *    clear. Surfaces say `NO_BULLETS_CAP_LINE` instead.
 *  - otherwise the score's Band (`statusOf`), with `gap` = `benchmark − score`.
 */
export type Overall =
  | { score: number; gap: number; standing: BandTone; band: Band; capped: false }
  | { score: number; gap: null; standing: 'watch'; band: 'Watch'; capped: true }
  | { score: null; gap: null; standing: 'not_scored'; band: null; capped: false };
/** What `overallOf` reads of a report. `benchmark` absent is `PINNED_BENCHMARK`, for a payload with none (Insights' sample). */
export type OverallInput = { overallScore: number | null; benchmark?: number; fixes: readonly { id: string }[] };
/**
 * A report's overall read against the bar, once, by every surface that draws
 * a Band: the funnel hero, its closing panel, phone bar and header pill, every
 * CTA label and analytics event (`FunnelPage`), Resume Health's score card and
 * Insights' readiness. Good begins at the benchmark (`statusOf`, `CONTEXT.md`
 * Band), and a report with no experience bullets never reaches it: the cap is
 * here and nowhere else, read off the wire's `NO_EXPERIENCE_BULLETS_FIX`.
 */
export function overallOf(report: OverallInput): Overall {
  const score = report.overallScore;
  if (score === null) return { score: null, gap: null, standing: 'not_scored', band: null, capped: false };
  const benchmark = report.benchmark ?? PINNED_BENCHMARK;
  const { tone, label } = statusOf(score, benchmark);
  if (tone === 'good' && report.fixes.some((fx) => fx.id === NO_EXPERIENCE_BULLETS_FIX)) return { score, gap: null, standing: 'watch', band: 'Watch', capped: true };
  return { score, gap: benchmark - score, standing: tone, band: label, capped: false };
}
/** A standing's colours: its Band's, or `NEUTRAL` when there is no Band. */
export const standingTone = (standing: ReportStanding) => (standing === 'not_scored' ? NEUTRAL : toneVars(standing));

// ── health rows: found by key, never by label (onboarding-tells-the-truth ticket 06) ──
/** The five rows the backend scorer emits, in its order. */
export const HEALTH_ROW_KEYS: readonly HealthRowKey[] = ['impact_language', 'quantified_results', 'keyword_coverage', 'formatting', 'length'];
/**
 * Each row's share of the overall score: the scorer's own weights, copied
 * because this package cannot import them, and pinned to them by
 * `backend/tests/contract/test_health_row_keys_drift.py`. The report orders
 * its Dimension cards by these, heaviest first (report-page-spec §3.2).
 * Nothing on the client recomputes a score from them.
 */
export const HEALTH_ROW_WEIGHTS: Readonly<Record<HealthRowKey, number>> = { impact_language: 0.25, quantified_results: 0.25, keyword_coverage: 0.25, formatting: 0.15, length: 0.1 };
/** A Dimension the analyzer measured: its `pct` is a number to draw. */
export type MeasuredHealthRow = HealthRow & { measured: true; pct: number };
/**
 * Whether a row carries a score (resume-360 ticket 16) — the one place that
 * reads it, so no screen re-checks `measured` and `pct` apart. The backend
 * never sends one without the other (`analysis.schemas.HealthRow` refuses
 * `measured` with a null `pct`), but the wire type cannot say so: it stays a
 * single interface because `test_health_row_shape_drift.py` pins it field by
 * field. A row that fails this is drawn as not measured, never as a number.
 */
export const isMeasured = (row: HealthRow): row is MeasuredHealthRow => row.measured && row.pct !== null;
/**
 * The rows a screen is about to draw, checked against the five the scorer
 * emits. A missing row is not drawn and not stood in for: it is reported on
 * the console, which is what `scripts/screenshots.mjs` writes to
 * `problems.json`. The funnel once found a row by its label and quoted 80 for
 * any label it could not find, so a backend rename rendered a plausible,
 * wrong number. A screen that needs one row on its own finds it with
 * `rows.find((r) => r.key === …)` and handles the miss — never a default.
 */
export function checkedHealthRows(rows: readonly HealthRow[]): readonly HealthRow[] {
  const missing = HEALTH_ROW_KEYS.filter((key) => !rows.some((r) => r.key === key));
  if (missing.length) console.error('Resume Health payload has no ' + missing.join(', ') + ' row; nothing is drawn in its place.');
  return rows;
}

/** The keyword-match section's three groups: green (`haveSkills`), red
 *  (`missingCoreSkills`, most target-role postings expect these) and amber
 *  (`missingSecondarySkills`, good to have) — resume-360 report page.
 *  Shared by the funnel report and Resume Health, the same two screens that
 *  already share `DimensionCard`/`FixCard`. `missingCore`/`missingSecondary`
 *  are `undefined` on a report stored before this shipped (never `[]`, which
 *  would mean "measured, nothing missing"); this falls back to the old single
 *  amber group — everything `learnSkills` named — rather than drawing an
 *  empty red column beside a non-empty amber one. */
export function keywordMatchGroups(h: Pick<HealthResult, 'haveSkills' | 'haveTotal' | 'learnSkills' | 'missingCoreSkills' | 'missingSecondarySkills'>): {
  have: readonly string[];
  /** Matched skills the server counted but did not name (ux-bug-pass ticket
   *  05): a guest's report names four. 0 when every one is named, or when
   *  `haveTotal` was not computed. */
  haveMore: number;
  missingCore: readonly string[];
  missingSecondary: readonly string[];
} {
  const hasSplit = h.missingCoreSkills !== undefined && h.missingSecondarySkills !== undefined;
  const have = h.haveSkills ?? [];
  return {
    have,
    haveMore: Math.max(0, (h.haveTotal ?? have.length) - have.length),
    missingCore: hasSplit ? h.missingCoreSkills! : [],
    missingSecondary: hasSplit ? h.missingSecondarySkills! : h.learnSkills ?? [],
  };
}

// ── a fix's impact: the weight of the Dimension it is ranked with (resume-360 ticket 11) ──
/** The one scale the top-3 cards' chips and the full list's groups share (report-page-spec §3.3, §3.6). */
export type Impact = 'high' | 'medium' | 'low';
export const IMPACT_LABELS: Readonly<Record<Impact, string>> = { high: 'High impact', medium: 'Medium impact', low: 'Low impact' };
/**
 * Each impact's ink on its soft, shared by the cards' chips and the full list's
 * group labels so a finding is one colour in both. Impact is a Dimension's
 * weight, not a severity, but it is *displayed* on the severity palette
 * (ux-bug-pass ticket 07, product decision 2026-10-03; spec "Severity colors"):
 * High red, Medium amber, Low grey, always beside its text label. Ink on soft:
 * 5.21, 4.50 and 5.12 to 1 (WCAG AA 4.5).
 */
const IMPACT_TONES: Readonly<Record<Impact, { ink: string; soft: string }>> = {
  high: { ink: 'var(--risk-ink,#b23b47)', soft: 'var(--risk-soft,#fdeff1)' },
  medium: { ink: 'var(--watch-ink,#9c6417)', soft: 'var(--watch-soft,#fdf3e6)' },
  low: { ink: 'var(--ink-muted,#626a7a)', soft: 'var(--surface-sunken,#f7f8fb)' },
};
/**
 * The weights at which a Dimension's impact becomes Medium and High (report-page-spec
 * §3.3: 0.25 High, 0.15 Medium, 0.10 Low). The one copy: the chip and the
 * group both read it through `impactOf`, and
 * `backend/tests/contract/test_fix_list_drift.py` pins it against the scorer's weights.
 */
export const IMPACT_THRESHOLDS: Readonly<Record<'high' | 'medium', number>> = { high: 0.25, medium: 0.15 };
/**
 * A fix's impact, read off the pinned weight of the Dimension it is ranked
 * with. **Never off its rank** (decided 2026-09-28): fixes are ranked by that
 * same weight, so rank 1 and rank 2 can be on one Dimension, and a chip by rank
 * would show a difference nothing measured. A weight belongs to the Dimension,
 * so it holds for one the report could not measure too.
 *
 * **None for a fix that moves nothing** (`movesDimension` false, the 11
 * review, decided 2026-09-30): naming the role, or chips no skill the role
 * screens for. A chip there would state an impact acting on it cannot have;
 * the card draws none and the full list puts the fix in Low (`fixGroups`).
 */
export function impactOf(fx: RankedFix): Impact | null {
  if (!fx.movesDimension) return null;
  const weight = HEALTH_ROW_WEIGHTS[fx.dimension];
  return weight >= IMPACT_THRESHOLDS.high ? 'high' : weight >= IMPACT_THRESHOLDS.medium ? 'medium' : 'low';
}
/** The report fields a fix's chip, worth line, group and share read. `allFixes` absent (a report stored before analyzer 2026.09.17, whose list the backend drops) means none of them is drawn. `benchmark` is for `overallOf`'s cap, which the worth line reads. */
export type FixReport = Pick<HealthResult, 'fixes' | 'moreFixes' | 'allFixes' | 'rows' | 'overallScore' | 'benchmark'>;
/**
 * Whether acting on a fix moves this report's score: it moves its Dimension
 * (`movesDimension`, the backend's per-report fact) and that Dimension was
 * measured, so its weight is in the overall. The one test the worth line, the
 * share and the closing line read.
 */
export function countsInScore(fx: RankedFix, report: FixReport): boolean {
  return fx.movesDimension && report.rows.some((r) => r.key === fx.dimension && isMeasured(r));
}
/**
 * The share of the current overall the given fixes' Dimensions carry, 0–1:
 * the weight of each one that counts in the score (`countsInScore`), once
 * each, over the weight of the Dimensions that were measured, which is what
 * `rules.overall_score` divides by. With every Dimension measured that is the
 * pinned weight itself (Quantified results, 0.25); with Keyword coverage not
 * measured it is 0.25 / 0.75. Null when there is no overall to carry a share
 * of (resume-360 ticket 20), when a row is missing from the payload (the
 * divisor would be a guess), or when none of them counts.
 */
function shareOf(fixes: readonly RankedFix[], report: FixReport): number | null {
  if (report.overallScore === null || HEALTH_ROW_KEYS.some((key) => !report.rows.some((r) => r.key === key))) return null;
  const carried = [...new Set(fixes.filter((fx) => countsInScore(fx, report)).map((fx) => fx.dimension))];
  if (!carried.length) return null;
  const weigh = (keys: readonly HealthRowKey[]) => keys.reduce((sum, key) => sum + HEALTH_ROW_WEIGHTS[key], 0);
  return weigh(carried) / weigh(report.rows.filter(isMeasured).map((r) => r.key));
}
const percent = (share: number) => Math.round(share * 100) + '%';
const listOf = (words: readonly string[]) => (words.length < 2 ? words.join('') : words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]);
/**
 * A card's worth line (report-page-spec §3.3).
 *
 * Where the wire carries the fix's `potentialPoints` (resume-360 ticket 12),
 * what following it alone is worth: "Worth up to +4 pts on Impact language."
 * ("+1 pt" in the singular). The backend computes it with the health scorer
 * and sends it only where the product can follow the fix with no new fact
 * and that raises the score on this Dimension alone, never as 0, never on a
 * fix that moves nothing, a Dimension not measured or a report with no overall. It is
 * one fix's "up to": nothing here or anywhere adds two of them up. A report
 * the no-bullets cap holds at Watch (`overallOf`) keeps the weight framing
 * instead: points there would claim a score its Band cannot show.
 *
 * Everywhere else, the weight framing, from the Dimension the fix is ranked
 * with. Every number is that Dimension's share of the score (`shareOf`), and
 * only where acting on the fix moves the score (`countsInScore`;
 * report-page-spec §0 rule 1):
 * - it counts, with an overall: "Moves Quantified results, which carries 25% of your score."
 * - not measured: its weight is not in the score, so no share is stated and
 *   the line says why ("On Keyword coverage, which wasn't measured, so it
 *   isn't part of your score."). "Moves" is dropped too: a fix on a row with
 *   nothing to read moves no number.
 * - it moves nothing (`movesDimension` false, the 11 review): it claims no
 *   move and no share, "Ranked with Formatting, though nothing in your score
 *   checks for this."
 * - no overall (ticket 20): there is no "your score" to carry a share of, so
 *   the line names the Dimension and stops ("Moves Formatting.", "Ranked
 *   with Formatting.").
 * Null when the payload has no such row, so nothing stands in for it.
 */
function worthLine(fx: RankedFix, report: FixReport): string | null {
  const row = report.rows.find((r) => r.key === fx.dimension);
  if (!row) return null;
  const points = fx.potentialPoints;
  if (points !== undefined && points > 0 && !overallOf(report).capped) return 'Worth up to +' + points + (points === 1 ? ' pt' : ' pts') + ' on ' + row.label + '.';
  const scored = report.overallScore !== null;
  if (!isMeasured(row)) return 'On ' + row.label + ", which wasn't measured" + (scored ? ", so it isn't part of your score." : '.');
  if (!countsInScore(fx, report)) return 'Ranked with ' + row.label + (scored ? ', though nothing in your score checks for this.' : '.');
  const share = shareOf([fx], report);
  return share === null ? 'Moves ' + row.label + '.' : 'Moves ' + row.label + ', which carries ' + percent(share) + ' of your score.';
}
/** The full list's entries by id: at most one fix per detector, so an id names one fix in a report. */
function rankedById(report: FixReport): Map<string, RankedFix> | null {
  return report.allFixes ? new Map(report.allFixes.map((fx) => [fx.id, fx])) : null;
}

/**
 * The closing panel's tier-2 claim (report-page-spec §5.4): the share of the
 * score the listed fixes move, "The top 3 fixes move Quantified results and
 * Keyword coverage, which carry 50% of your score." Each Dimension counts
 * once, however many fixes move it, and only a fix that counts in the score
 * (`countsInScore`) counts: "1 of the top 3 fixes moves …" when the others
 * move a Dimension not measured, or move nothing (the 11 review). Null with no
 * overall (ticket 20), no full list, or nothing that counts to name — the
 * panel then falls to tier 3, the gap in its lead.
 */
export function topFixesShare(report: FixReport): string | null {
  const byId = rankedById(report);
  const listed = byId ? report.fixes.map((fx) => byId.get(fx.id)) : [];
  if (!listed.length || listed.some((fx) => !fx)) return null;
  const counted = (listed as RankedFix[]).filter((fx) => countsInScore(fx, report));
  const dimensions = [...new Set(counted.map((fx) => fx.dimension))];
  const share = shareOf(counted, report);
  if (share === null) return null;
  const n = listed.length;
  const k = counted.length;
  const whole = n === 1 ? (report.moreFixes ? 'The top fix' : 'The fix above') : report.moreFixes ? 'The top ' + n + ' fixes' : 'The ' + n + ' fixes above';
  const subject = k === n ? whole : k + ' of ' + whole.replace(/^The /, 'the ');
  const verb = k === 1 ? 'moves' : k === n && dimensions.length === 1 ? 'all move' : 'move';
  const labels = dimensions.map((key) => report.rows.find((r) => r.key === key)?.label ?? key);
  return subject + ' ' + verb + ' ' + listOf(labels) + ', which ' + (dimensions.length === 1 ? 'carries ' : 'carry ') + percent(share) + ' of your score.';
}

/**
 * One line of the full list: the fix's title, then where it points in the
 * résumé (ux-bug-pass ticket 06) — its quote, clipped to `QUOTE_CLIP` with the
 * whole line in `fullQuote` for the row's `title`, and its section plus the
 * entry it is about. Every row has a quote or an entry label (`fixGroups`).
 */
export interface FixGroupEntry {
  id: string;
  title: string;
  section: string;
  /** The quote on one line, clipped with an ellipsis; '' when the fix names only its entry. */
  quote: string;
  /** The quote on one line, whole: the row's hover and screen-reader text. */
  fullQuote: string;
  /** "Senior Analyst · Acme"; '' for a fix about no one entry. */
  entryLabel: string;
}
/** A row's quote is clipped past this many characters (ux-bug-pass ticket 06: "~90"). */
const QUOTE_CLIP = 90;
/** A quote as one line (a multi-line quote, one role's bullets, joined by spaces), clipped at a word with an ellipsis past `max`. */
function clipQuote(quote: string, max = QUOTE_CLIP): string {
  const line = quote.replace(/\s+/g, ' ').trim();
  if (line.length <= max) return line;
  const cut = line.slice(0, max - 1);
  const word = cut.lastIndexOf(' ');
  return (word > max * 0.6 ? cut.slice(0, word) : cut).replace(/[\s,;:.]+$/, '') + '…';
}
/**
 * A fix as a row of the full list, or null when it points at nothing: no quote
 * and no entry (ux-bug-pass ticket 06). The backend lists none such since
 * analyzer 2026.10.04 (`test_analysis_fix_anchors.py`, which also checks
 * every quote is verbatim, since the client has no résumé text to check
 * against); a report stored before then can still carry one ("Add a summary"),
 * and a row that says where nothing is reads as generic advice.
 */
const warnedAnchorless = new Set<string>();
function fixRow(fx: RankedFix): FixGroupEntry | null {
  const fullQuote = fx.quote.replace(/\s+/g, ' ').trim();
  const entryLabel = fx.entryLabel?.trim() ?? '';
  if (!fullQuote && !entryLabel) {
    // Once per id: a screen recomputes its list on every render.
    if (!warnedAnchorless.has(fx.id)) console.warn('fixGroups: left out a fix that points at nothing in the résumé', fx.id);
    warnedAnchorless.add(fx.id);
    return null;
  }
  return { id: fx.id, title: fx.title, section: fx.section, quote: clipQuote(fullQuote), fullQuote, entryLabel };
}
export interface FixGroup {
  key: Impact;
  label: string;
  /** The label's tone, the card chip's own (`IMPACT_TONES`). */
  ink: string;
  soft: string;
  count: number;
  entries: FixGroupEntry[];
}
/** The full list past the cards as a screen draws it: its heading and its groups. */
export interface FixGroupList {
  /** "+N more found", N counting the entries below, so it is what the list holds. */
  heading: string;
  groups: FixGroup[];
}
/** The count line of fixes past the cards: the full list's heading, and a stored report's strip. */
export const moreFound = (n: number) => '+' + n + ' more found';
/**
 * The full list past the listed cards, as collapsed groups on the chips' scale
 * (report-page-spec §3.6): High, then Medium, then Low, an empty group left
 * out, each fix in the wire's rank order (the backend sends it ranked). A fix
 * that moves nothing has no impact (`impactOf`) and is listed in Low, after
 * the fixes there that move their Dimension, since the backend ranks it after
 * every one of them (the 11 review). The listed cards are never repeated
 * inside. Null without a full list or with nothing past the cards — the screen
 * then draws its "+N more found" strip, or nothing.
 */
export function fixGroups(report: FixReport): FixGroupList | null {
  if (!report.allFixes || report.moreFixes <= 0) return null;
  const listed = new Set(report.fixes.map((fx) => fx.id));
  const rest = report.allFixes.filter((fx) => !listed.has(fx.id));
  const groups = (['high', 'medium', 'low'] as const).map((key) => {
    const entries = rest.filter((fx) => (impactOf(fx) ?? 'low') === key).map(fixRow).filter((e): e is FixGroupEntry => e !== null);
    return { key, label: IMPACT_LABELS[key], ...IMPACT_TONES[key], count: entries.length, entries };
  }).filter((g) => g.count > 0);
  const shown = groups.reduce((n, g) => n + g.count, 0);
  return groups.length ? { heading: moreFound(shown), groups } : null;
}

/**
 * A fix as the funnel's card and Resume Health's list both draw it: numbered,
 * and carrying exactly the payload's title, section, quote and reason. One
 * composition, so the two screens cannot come to say different things about
 * the same finding (onboarding-tells-the-truth ticket 01). Nothing here
 * applies a fix or scores one. Both draw it with `components/FixCard`
 * (resume-360 ticket 17).
 *
 * With the report's full list (resume-360 ticket 11) a card also carries its
 * **impact chip** and its **worth line**, both read off the Dimension the fix
 * is ranked with and whether it moves it (`impactOf`, `worthLine`), and both
 * absent for a report stored before that list shipped.
 *
 * Resume Health also passes its health payload's suggestion keys, and a Pro
 * account's card then carries its **Pro rewrite suggestion** (resume-360
 * ticket 24): advice shown under the account's own line, never written into
 * the document (docs/adr/0014). The funnel passes none, so its cards never
 * carry one.
 */
export interface FixCardInput {
  /** Rank, from 1: the number the card shows. */
  n: number;
  title: string;
  section: string;
  /** The document's own line; '' for the detectors that quote nothing, and then the card draws no quote. */
  quote: string;
  why: string;
  /** The chip's words and tone, from the Dimension's weight; absent without a full list, and on a fix that moves nothing. */
  impact?: { key: Impact; label: string; ink: string; soft: string };
  /** The worth line (`worthLine`): the fix's computed points where the wire has them, else the weight framing; absent without a full list. */
  worth?: string;
  /** Present only on a Pro account's rewritable fix, while its rewrite is being written or once it is. */
  suggestion?: FixSuggestion;
}
/** The card's words around it are the prototype's (`FixCard.dc.html`). */
export interface FixSuggestion {
  state: 'pending' | 'ready';
  /** The rewrite as sent; '' while pending. */
  rewrite: string;
}
/**
 * The fixes a Pro rewrite is ever written for: `analysis.rules.REWRITABLE_FIXES`,
 * those whose quote is a line of the account's own prose. `pending` names a
 * rewrite on exactly these cards. Pinned to the backend's set by
 * `backend/tests/contract/test_rewrite_suggestions_drift.py`.
 */
export const REWRITABLE_FIX_IDS: readonly string[] = ['metric_missing_recent_role', 'summary_lacks_role', 'weak_verb_bullets', 'no_metrics_entry', 'long_bullet'];
function suggestionFor(fx: HealthFix, suggestions: HealthSuggestions): FixSuggestion | null {
  if (suggestions.aiSuggestionsStatus === 'pending') return REWRITABLE_FIX_IDS.includes(fx.id) && fx.quote.trim() ? { state: 'pending', rewrite: '' } : null;
  // `ready` with no rewrite for this fix is silence, never "none found": an
  // empty list is also how a failed model call reaches the client.
  const rewrite = suggestions.aiSuggestionsStatus === 'ready' ? suggestions.aiSuggestions?.find((s) => s.fixId === fx.id)?.rewrite : undefined;
  return rewrite ? { state: 'ready', rewrite } : null;
}
/** The report's listed fixes as cards. `suggestions` is a health body's
 *  suggestion keys, for either plan: a Free body has neither, so its cards
 *  carry none. The funnel passes none. */
export function fixCards(report: FixReport, suggestions?: HealthSuggestions): FixCardInput[] {
  const byId = rankedById(report);
  return report.fixes.map((fx, i) => {
    const card: FixCardInput = { n: i + 1, title: fx.title, section: fx.section, quote: fx.quote, why: fx.why };
    const ranked = byId?.get(fx.id);
    if (ranked) {
      const impact = impactOf(ranked);
      if (impact) card.impact = { key: impact, label: IMPACT_LABELS[impact], ...IMPACT_TONES[impact] };
      const worth = worthLine(ranked, report);
      if (worth) card.worth = worth;
    }
    const suggestion = suggestions ? suggestionFor(fx, suggestions) : null;
    return suggestion ? { ...card, suggestion } : card;
  });
}

// The tailoring projection (each level's projected score, and the recommended
// level) is the server's alone: `GET /v1/jobs/{id}`'s `tailoredProjectedByLevel`
// and `recommendedLevel` (ux-bug-pass ticket 28's follow-up). The client copy of
// the formula that lived here knew no job skill addition and disagreed with
// the bar beside it.

export interface LevelDef {
  key: TailorLevel;
  title: string;
  plus: string;
  body: string;
  /** What the level says while the Skill Bank is on (skill-bank-tailor 14, ADR 0029). `body` stays for the setting off. */
  bankBody: string;
}
/** The card's sentence: the Skill Bank's words while the setting is on, the older words while it is off. */
export const levelBody = (l: LevelDef, bankOn: boolean): string => (bankOn ? l.bankBody : l.body);
export const DETAIL_LEVELS: LevelDef[] = [
  { key: 'polish', title: 'Polish', plus: '+', body: 'Sharpen wording, formatting and structure. Adds only skills you added for this job.', bankBody: 'Better heading and summary, formatting, wording and order. Adds no skills.' },
  { key: 'match', title: 'Match', plus: '++', body: 'Polish, plus this job’s keywords, using only skills your résumé shows or you added for this job.', bankBody: "Polish, plus your skills in the job's own words, for example AWS as Amazon Web Services." },
  { key: 'max', title: 'Max Match', plus: '+++', body: 'Match, plus your most relevant bullets moved first and expanded.', bankBody: 'Match, plus broader terms your skills imply, for example AWS as cloud. Sharper title and summary. Cuts content that does not fit this job.' },
];
export function levelDef(key: string | null | undefined): LevelDef {
  return DETAIL_LEVELS.find((l) => l.key === key) || DETAIL_LEVELS[1];
}

// ── what a level would do, beside what it promises to reach (ticket 22) ──
/**
 * One line describing the work a level would do: the skills it would list and
 * the roles it would rewrite.
 *
 * ⚠️ **Never a bullet count.** The model decides how many lines a role comes
 * back with, so a number here before the call would be a guess — which is why
 * the payload has no such field to render.
 *
 * `null` while the preview is in flight, so the card shows nothing rather
 * than a placeholder that later changes under the reader.
 */
export function levelWork(plan: TailorLevelPlan | undefined): string | null {
  if (!plan) return null;
  const shown = plan.skillsAdded.slice(0, 3).join(', ');
  const rest = plan.skillsAdded.length - 3;
  const skills = plan.skillsAdded.length === 0 ? 'Adds no new skills' : 'Adds ' + shown + (rest > 0 ? ' +' + rest + ' more' : '');
  // "no roles to rewrite" rather than "0 of 0 roles": a resume with no
  // experience is a different statement from one this posting passes over.
  const roles = plan.entriesTotal === 0
    ? 'no roles to rewrite'
    : 'rewrites ' + plan.entriesRewritten + ' of ' + plan.entriesTotal + (plan.entriesTotal === 1 ? ' role' : ' roles');
  return skills + ' · ' + roles;
}
export interface TemplateOption {
  key: TemplateId;
  title: string;
  swatch: string;
}
export const DETAIL_TEMPLATES: TemplateOption[] = [
  { key: 'template1', title: 'Classic', swatch: '#ffffff' },
  { key: 'template2', title: 'Split Column', swatch: 'linear-gradient(90deg,#2a3142 38%,#fff 38%)' },
  { key: 'template3', title: 'Modern Sidebar', swatch: 'linear-gradient(180deg,#2a3142 30%,#fff 30%)' },
  { key: 'template4', title: 'Executive', swatch: 'repeating-linear-gradient(#fff 0 3px,#e2e6ec 3px 4px)' },
];
export function templateOption(key: string | null | undefined): TemplateOption {
  return DETAIL_TEMPLATES.find((t) => t.key === key) || DETAIL_TEMPLATES[0];
}

// ── application status ──
export const STATUS_OPTIONS = [
  { value: 'applied', label: 'Applied' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer', label: 'Offer' },
  { value: 'rejected', label: 'Rejected' },
] as const;
export const STATUS_TONE: Record<string, { bg: string; ink: string; line: string }> = {
  applied: { bg: 'var(--surface-sunken)', ink: 'var(--ink-secondary)', line: 'var(--line-strong)' },
  interview: { bg: 'var(--watch-soft)', ink: 'var(--watch-ink)', line: 'color-mix(in srgb, var(--watch) 35%, #fff)' },
  offer: { bg: 'var(--good-soft)', ink: 'var(--good-ink)', line: 'color-mix(in srgb, var(--good) 35%, #fff)' },
  rejected: { bg: 'var(--risk-soft)', ink: 'var(--risk-ink)', line: 'color-mix(in srgb, var(--risk) 35%, #fff)' },
};

// ── dates ──
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'saved';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (isNaN(days)) return 'saved';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return days + ' days ago';
  if (days < 14) return 'last week';
  if (days < 60) return Math.round(days / 7) + ' weeks ago';
  return Math.round(days / 30) + ' months ago';
}
export function formatAppliedAt(iso: string | null | undefined, compact: boolean): string {
  if (!iso) return 'Date unknown';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return 'Date unknown';
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  const date = dt.toLocaleDateString('en-US', compact && sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
  return date + ' · ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
export function appliedWhenLabel(iso: string | null | undefined): string {
  if (!iso) return 'Applied';
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return 'Applied today · ' + time;
  return 'Applied ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' · ' + time;
}
export function fmtDay(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
export function fmtShortDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
export function localDateTimeValue(dt: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return dt.getFullYear() + '-' + p(dt.getMonth() + 1) + '-' + p(dt.getDate()) + 'T' + p(dt.getHours()) + ':' + p(dt.getMinutes());
}
export function postedLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return '';
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return h + 'h';
  return Math.floor(h / 24) + 'd';
}

// ── money ──
// Every price is US dollars and says so (legal-pages ticket 07): a bare "$" reads
// as their own dollar to a visitor in Canada or Australia. These two are the only
// place a currency is written, so no screen can show a price without it.
const CURRENCY = 'US$';
export function money(cents: number): string {
  const dollars = cents / 100;
  return CURRENCY + (Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2));
}
export function money2(cents: number): string {
  return CURRENCY + (cents / 100).toFixed(2);
}

export function hostOf(url: string | null | undefined): string {
  try {
    return new URL(url || '').hostname.replace(/^www\./, '');
  } catch {
    return 'the job posting';
  }
}
export function pluralize(n: number, one: string, many = one + 's'): string {
  return n === 1 ? '1 ' + one : n + ' ' + many;
}
export function formatBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}
